/*
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */
import { useEffect, useMemo, useRef } from 'react';

import { withTheme } from 'styled-components';
import OktaFormWrapper from './OktaFormWrapper';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useOktaAuth } from '@okta/okta-react';
import OktaSignIn, { RenderResult, RenderResultSuccessOIDC, WidgetOptions } from '@okta/okta-signin-widget';
import { selectIsAuthenticated, selectLoginConfig } from '../../store';
import { setAuthTokens } from '../../store/session/actions';
import { LOGIN_REDIRECT_URI, LOGIN_ROUTE, SCOPES_REQUIRED } from '../../utils/AuthUtils';
import { MenuItems } from '../common/layout/menu/MenuItems';
import logoImage from '../../static/images/logo.png';
import { LoginCallbackErrorStatus } from './CustomLoginCallback';
import { CircularProgress } from '@mui/material';

export const INITIAL_WIDGET_FORM_NAME: string = 'identify';
export const DOD_ID_FORM_NAME: string = 'enroll-profile';
export const DEFAULT_DOD_ID_VALUE: string = 'User Skipped DoD ID Input';
export const CALLBACK_ERROR_MESSAGE: string = 'Failed to sign in. Please try again or contact support.';
export const DOD_ID_FORM_INFO_TEXT: string =
  'Enter your DoD ID to enable CAC card authentication for future logins. Otherwise, skip this step.';
export const CAC_FORM_NAME: string = 'piv-idp';

export const SELECTORS = {
  buttonBar: '.o-form-button-bar',
  dodIdSkipButton: 'dod-id-skip-button',
  dodIdInput: 'input[name="userProfile.cac"]',
  dodIdSubmitButton: 'input[type="submit"]',
  fieldsets: 'o-form-fieldset o-form-label-top',
  customForgotPassword: 'custom-forgot-password',
  defaultForgotPassword: 'link js-forgot-password',
  formTitle: 'okta-form-title',
  errorContainer: '.o-form-error-container',
  errorsExist: 'o-form-has-errors',
  formInfoContainer: '.o-form-info-container',
  formInfoTextParagraph: 'dod-id-info-text',
  signInWithCacButton: 'sign-in-with-idp',
  retryButton: 'input[value="Retry"]',
};

const OktaForm = () => {
  const loginConfig = useSelector(selectLoginConfig);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const dispatch = useDispatch();
  const oktaCtx = useOktaAuth();
  const history = useHistory();
  const curURL = useMemo(() => new URL(window.location.href), []);
  const isResetPasswordRoute = curURL.href.indexOf('reset') !== -1;
  // Check for error callback (CustomLoginCallback will trigger with errorComponent that sets the error status if applicable)
  const doesCallbackErrorExistRef = useRef<boolean>((history.location.state as LoginCallbackErrorStatus)?.loginError);

  // Runs on mount to clear the loginError property from the history state so we don't show it again on refresh
  useEffect(() => {
    if (doesCallbackErrorExistRef.current) {
      history.replace(LOGIN_ROUTE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to add a skip button on the DoD ID form that auto-fills with default value and submits the form if user does not want to use CAC auth
  function addDoDIdFormSkipOption() {
    const buttonBar = document.querySelector<HTMLDivElement>(SELECTORS.buttonBar);
    const existingSkipButton = document.getElementById(SELECTORS.dodIdSkipButton);
    // Don't add another button on refresh
    if (!buttonBar || existingSkipButton) {
      return;
    }
    const skipButton = document.createElement('button');
    skipButton.id = SELECTORS.dodIdSkipButton;
    skipButton.className = 'button skip-button';
    skipButton.textContent = 'Skip';

    skipButton.addEventListener('click', () => {
      const input = document.querySelector<HTMLInputElement>(SELECTORS.dodIdInput);
      const submitBtn = document.querySelector<HTMLInputElement>(SELECTORS.dodIdSubmitButton);

      if (input && submitBtn) {
        input.value = DEFAULT_DOD_ID_VALUE;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        submitBtn.click();
      }
    });
    buttonBar.appendChild(skipButton);
  }

  // Function to add text above the form label for the DoD ID input to give context
  function addDoDIdFormInfoText() {
    const formInfoContainer = document.querySelector<HTMLDivElement>(SELECTORS.formInfoContainer);
    const existingFormInfoText = document.getElementById(SELECTORS.formInfoTextParagraph);
    if (!formInfoContainer || existingFormInfoText) {
      return;
    }
    const infoText = document.createElement('p');
    infoText.id = SELECTORS.formInfoTextParagraph;
    infoText.className = SELECTORS.formInfoTextParagraph;
    infoText.textContent = DOD_ID_FORM_INFO_TEXT;
    formInfoContainer.appendChild(infoText);
  }

  // Function that inserts an Okta error message into the Okta widget form with the same class names as default Okta errors
  function insertOktaErrorMessage(message: string) {
    const container = document.querySelector<HTMLDivElement>(SELECTORS.errorContainer);
    if (!container) {
      return;
    }
    container.classList.add(SELECTORS.errorsExist);

    const customErrorDiv = document.createElement('div');

    const infobox = document.createElement('div');
    infobox.className = 'okta-form-infobox-error infobox infobox-error';
    infobox.setAttribute('role', 'alert');

    const icon = document.createElement('span');
    icon.className = 'icon error-16';

    const p = document.createElement('p');
    p.textContent = message;
    infobox.append(icon, p);
    customErrorDiv.appendChild(infobox);
    container.appendChild(customErrorDiv);
  }

  useEffect(() => {
    // Grab URL params needed to support Email Magic Links
    let searchParams = curURL.searchParams;
    let emailMagicLinkParams = {
      otp: searchParams.get('otp'),
      state: searchParams.get('state') || undefined,
    };

    const widgetOptions: WidgetOptions = {
      // String configuration
      logoText: 'Sign in to your account',

      i18n: {
        en: {
          'primaryauth.title': 'Sign in to your account',
          'primaryauth.username.placeholder': 'Email address',
          'primaryauth.password.placeholder': 'Password',
          'oie.remember': 'Keep me signed in.',
          'oie.password.challenge.title': 'Enter your password',
          goback: isResetPasswordRoute ? 'Return to password reset.' : 'Return to sign in.',
          'oie.email.verify.subtitle.text.with.email':
            'Click the button below to request your verification code by email at <$1>{0}</$1>.',
          'oie.email.verify.subtitle.text.without.email':
            'Click the button below to request your verification code by email.',
          'oie.email.verify.primaryButton': 'Request verification code',
          'email.code.not.received': "Haven't received the email?",
          'email.button.resend': 'Resend it.',
          'idx.transferred.to.new.tab': 'Verification continued in another window. You may now close this window.',
          'idx.enter.otp.in.original.tab': 'Enter this code in the original login window:',
          'oie.email.mfa.title': 'Verify your account',
          'idx.invalid.device.code': 'Invalid code. Check your code and try again.',
          'api.authn.error.PASSCODE_INVALID': 'Invalid code. Check your code and try again.',
          'model.validation.field.blank': 'This field cannot be left blank.',
          'oform.errorbanner.title': "We've found some errors. Please make corrections and try again.",
          'error.username.required': 'Please enter an email',
          'error.password.required': 'Please enter a password',
          'errors.E0000004': 'Sign in failed!',
          'piv.cac.card.insert': 'Please insert your CAC card and select the user certificate.',
          'piv.cac.card': 'Sign in with CAC card',
          'piv.cac.title': 'CAC Card',
          'piv.card.error.empty':
            'No certificate selected.\n\nTry again by closing all current browser sessions and reopening to select a certificate. Please contact support if unable to sign in.',
          'piv.card.error.invalid':
            'Certificate validation failed.\n\nTry again by closing all current browser sessions and reopening to try a different certificate. Please contact support if unable to sign in.',
        },
      },

      el: `#okta-login-form`,

      issuer: loginConfig.oidcIssuer,
      clientId: loginConfig.oidcClientId,
      redirectUri: LOGIN_REDIRECT_URI,
      scopes: SCOPES_REQUIRED,
      ...emailMagicLinkParams,
    };

    // Only setting the flow explicity for reset password allows the widget to use any other flow (login, enrollProfile, etc) for other routes
    if (isResetPasswordRoute) {
      widgetOptions.flow = 'resetPassword';
    }

    const widget = new OktaSignIn({
      ...widgetOptions,
    });

    /* istanbul ignore next -- @preserve */
    widget.on('afterRender', function (context: any) {
      const formName = context.formName;
      // If we have an error and on the initial form, show it
      if (doesCallbackErrorExistRef.current && formName === INITIAL_WIDGET_FORM_NAME) {
        insertOktaErrorMessage(CALLBACK_ERROR_MESSAGE);
        doesCallbackErrorExistRef.current = false; // only show it once
      }
      // Add a skip button/extra text when on the DoD ID form for CAC
      if (formName === DOD_ID_FORM_NAME) {
        addDoDIdFormInfoText();
        addDoDIdFormSkipOption();
      }
      // Disable the Retry button on CAC error, as the cert will be cached and retry will do nothing but retrigger error
      if (formName === CAC_FORM_NAME) {
        const retryButton = document.querySelector<HTMLInputElement>(SELECTORS.retryButton);
        if (retryButton) {
          retryButton.disabled = true;
        }
      }
      // Hide the CAC card sign in button since we don't want users to log in this way
      const cacSignInDiv = document.getElementsByClassName(SELECTORS.signInWithCacButton)[0];
      if (cacSignInDiv) {
        cacSignInDiv.setAttribute('style', 'display:none');
      }
      // hide keep me signed in checkbox if on reset password route
      const fieldsets = document.getElementsByClassName(SELECTORS.fieldsets);
      if (fieldsets.length > 1) {
        const rememberMe = fieldsets[1];
        if (isResetPasswordRoute) {
          rememberMe.setAttribute('style', 'display:none; margin:0');
        } else rememberMe.setAttribute('style', '');
      }

      // Remove duplicate forgot password link if it already exists
      const customForgotPasswordLink = document.getElementsByClassName(SELECTORS.customForgotPassword)[0];
      if (customForgotPasswordLink?.parentElement) {
        const defaultForgotPassword = document.getElementsByClassName(SELECTORS.defaultForgotPassword);
        const title = document.getElementsByClassName(SELECTORS.formTitle)[0];
        let newDisplayStyle = '';
        // Hide custom forgot password link if default exists, or if the title is "Get a verification email"
        if (defaultForgotPassword.length !== 0 || (title && title.textContent === 'Get a verification email')) {
          newDisplayStyle = 'display:none';
        }
        customForgotPasswordLink.parentElement.setAttribute('style', newDisplayStyle);
      }
    });

    /* istanbul ignore next -- @preserve */
    widget.authClient.isAuthenticated().then(isAuthenticated => {
      if (!isAuthenticated) {
        widget.showSignIn().then((authResult: RenderResult) => {
          if (authResult.status === 'SUCCESS') {
            const oidcAuthResult = authResult as RenderResultSuccessOIDC;

            // Set tokens in redux store
            dispatch(setAuthTokens(oidcAuthResult.tokens!));

            // Set tokens in Okta Security context & redirect. This causes
            // the tokens to be automagically refreshed by the SecurityProvider
            // component's OktaAuth.authStateManager.
            oktaCtx.oktaAuth.handleLoginRedirect(oidcAuthResult.tokens!);
          }
        });
      }
    });
    return () => {
      widget.remove();
    };
  }, [dispatch, loginConfig, oktaCtx, curURL, isResetPasswordRoute]);

  useEffect(() => {
    if (isAuthenticated) {
      // Push user to fleet view if authenticated
      history.push(`/${MenuItems.fleet}`);
    }
  }, [isAuthenticated, history]);
  return (
    <OktaFormWrapper>
      <img src={logoImage} className="viasat-logo" alt="Viasat Logo" />
      <div id="okta-login-form"></div>
      <CircularProgress classes={{ root: 'login-spinner' }} />
      <p className="help-link">
        {!isResetPasswordRoute ? (
          <a href={`${LOGIN_ROUTE}/reset`} className="helplink custom-forgot-password">
            Forgot password?
          </a>
        ) : (
          <a href={`${LOGIN_ROUTE}`} className="helplink">
            Return to sign in
          </a>
        )}
      </p>
      {loginConfig.helpEmail && (
        <p className="help-link">
          Having trouble signing in? <a href={'mailto:' + loginConfig.helpEmail}>Contact us.</a>
        </p>
      )}
    </OktaFormWrapper>
  );
};

export default withTheme(OktaForm);
